import config from '@/config';
import { useEffect, useState } from 'react';
/**
 * React hook that returns if the application is online
 * is click outside the reference element
 *
 * @param host - Is the host to see if it's reachable
 * @return The service worker
 *
 */
export function useIsOnline(h?: string): boolean {
	const host: string = h ?? config.build.host;
	const [onlineStatus, setOnlineStatus] = useState<boolean>(navigator.onLine);
	useEffect(() => {
		const checkReachability = () => {
			isReachable(host)
				.then(() => setOnlineStatus(true))
				.catch(() => setOnlineStatus(false));
		};

		// The browser's `online` event only means a network interface came
		// up -- a captive portal, a VPN that's connected but not routing, or
		// DNS trouble all fire it while the actual backend stays
		// unreachable. Re-verify instead of trusting it directly, same
		// reasoning as sw.version.ts's update check.
		checkReachability();

		const offlineHandler = () => {
			setOnlineStatus(false);
		};

		window.addEventListener('online', checkReachability);
		window.addEventListener('offline', offlineHandler);

		return () => {
			window.removeEventListener('online', checkReachability);
			window.removeEventListener('offline', offlineHandler);
		};
	}, [host]);
	return onlineStatus;
}

/**
 * Whether `url`'s real server actually answered -- not just whether some
 * network interface is up. Uses normal (not no-cors) mode: the backend
 * already sends `Access-Control-Allow-Origin: *`, so a resolved response
 * proves we reached it, even a non-2xx one (many GraphQL hosts don't
 * support HEAD and answer 400/405 -- still proof of reachability, not
 * proof of failure). A captive portal's injected page won't have the
 * right CORS headers for our origin, so it makes the fetch reject rather
 * than resolve as an indistinguishable "opaque" success the way a
 * no-cors request would -- opaque responses can't be inspected at all,
 * so a no-cors check can't tell a captive portal from the real host, and
 * treats a hard network failure as the only "offline" signal. Bounded by
 * an explicit timeout so a hanging/degraded connection can't leave
 * "am I online" unresolved indefinitely.
 */
export async function isReachable(
	url: string,
	timeoutMs = 5000,
): Promise<boolean> {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
	try {
		await fetch(url, { method: 'HEAD', signal: controller.signal });
		return true;
	} catch {
		return false;
	} finally {
		clearTimeout(timeoutId);
	}
}
